/**
 * Audio-only LiveKit room for the agent. `livekit-client` is loaded lazily so
 * the console bundle stays small and tests can inject a fake connector.
 */
export interface VoiceRoom {
  setMuted(muted: boolean): Promise<void>;
  setMicrophone(deviceId: string): Promise<void>;
  leave(): Promise<void>;
  /** Subscribe to local mic level 0..1 (polled). */
  onLevel(cb: (level: number) => void): () => void;
  /** Subscribe to remote participant count changes. */
  onParticipants(cb: (n: number) => void): () => void;
}

export interface VoiceMicrophone {
  deviceId: string;
  label: string;
  isDefault: boolean;
}

export type VoiceConnector = (url: string, token: string, microphoneId?: string) => Promise<VoiceRoom>;

let connector: VoiceConnector = defaultConnector;

/** Replace the connector (tests). Returns a restore function. */
export function setVoiceConnector(fn: VoiceConnector): () => void {
  const prev = connector;
  connector = fn;
  return () => {
    connector = prev;
  };
}

export function connectVoice(url: string, token: string, microphoneId?: string): Promise<VoiceRoom> {
  return connector(url, token, microphoneId);
}

export async function listMicrophones(mediaDevices: MediaDevices | undefined = navigator.mediaDevices): Promise<VoiceMicrophone[]> {
  if (!mediaDevices?.enumerateDevices) return [{ deviceId: "default", label: "Default microphone", isDefault: true }];
  const devices = (await mediaDevices.enumerateDevices()).filter((d) => d.kind === "audioinput");
  const microphones = devices.map((d, i) => ({
    deviceId: d.deviceId,
    label: d.deviceId === "default" ? "Default microphone" : d.label || `Microphone ${i + 1}`,
    isDefault: d.deviceId === "default",
  }));
  if (!microphones.some((d) => d.isDefault)) microphones.unshift({ deviceId: "default", label: "Default microphone", isDefault: true });
  return microphones;
}

async function defaultConnector(url: string, token: string, microphoneId = "default"): Promise<VoiceRoom> {
  const lk = await import("livekit-client");
  const room = new lk.Room({ adaptiveStream: false, dynacast: false });
  await room.connect(url, token, { autoSubscribe: true });
  if (microphoneId !== "default") await room.switchActiveDevice("audioinput", microphoneId, true);
  await room.localParticipant.setMicrophoneEnabled(true);
  // Remote audio tracks attach to hidden <audio> elements so the agent hears the user.
  const attached = new Set<HTMLMediaElement>();
  room.on(lk.RoomEvent.TrackSubscribed, (track) => {
    if (track.kind === lk.Track.Kind.Audio) {
      const el = track.attach();
      el.style.display = "none";
      document.body.appendChild(el);
      attached.add(el);
    }
  });
  room.on(lk.RoomEvent.TrackUnsubscribed, (track) => {
    for (const el of track.detach()) {
      el.remove();
      attached.delete(el);
    }
  });
  return {
    async setMuted(muted) {
      await room.localParticipant.setMicrophoneEnabled(!muted);
    },
    async setMicrophone(deviceId) {
      await room.switchActiveDevice("audioinput", deviceId, deviceId !== "default");
    },
    async leave() {
      for (const el of attached) el.remove();
      attached.clear();
      await room.disconnect();
    },
    onLevel(cb) {
      const t = setInterval(() => cb(room.localParticipant.audioLevel ?? 0), 250);
      return () => clearInterval(t);
    },
    onParticipants(cb) {
      const emit = () => cb(room.remoteParticipants.size);
      room.on(lk.RoomEvent.ParticipantConnected, emit);
      room.on(lk.RoomEvent.ParticipantDisconnected, emit);
      emit();
      return () => {
        room.off(lk.RoomEvent.ParticipantConnected, emit);
        room.off(lk.RoomEvent.ParticipantDisconnected, emit);
      };
    },
  };
}
